<template>
  <div class="page-content-wrapper">
    <!-- Stats Cards -->
    <div class="row mb-2">
      <div 
        class="col-md-3 col-sm-6 mb-3 stats-card-wrapper" 
        @click="toggleFilter('in_progress')"
        :class="{ 'stats-card-active': activeFilters.includes('in_progress') }"
      >
        <IconCard
          title="In Progress"
          :count="statsData.inProgressCount"
          icon_class="bi bi-hourglass-split"
        />
      </div>
      <div 
        class="col-md-3 col-sm-6 mb-3 stats-card-wrapper" 
        @click="toggleFilter('unread')"
        :class="{ 'stats-card-active': activeFilters.includes('unread') }"
      >
        <IconCard
          title="Unread"
          :count="statsData.unreadCount"
          icon_class="bi bi-envelope"
        />
      </div>
    </div>

    <transition name="slide-down">
      <div
        v-if="selectedApplication"
        class="card rounded-3 shadow mb-3 application-view-card"
      >
        <div class="card-body p-4">
          <!-- Header with close button -->
          <div
            class="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom"
          >
            <div class="d-flex align-items-center">
              <div
                :class="[
                  'avatar-icon me-3',
                  selected_entity_type === 'individual'
                    ? 'avatar-individual'
                    : 'avatar-company',
                ]"
              >
                <i
                  :class="
                    selected_entity_type === 'individual'
                      ? 'bi bi-person'
                      : 'bi bi-building'
                  "
                ></i>
              </div>
              <div>
                <h5 class="mb-0 fw-bold">{{ selectedApplication.name }}</h5>
                <small class="text-muted">{{
                  selectedApplication.workflow_name || "Application Details"
                }}</small>
              </div>
            </div>
            <button
              class="btn btn-sm btn-outline-secondary rounded-3"
              @click="closeApplicationView"
              title="Close View"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          <!-- viewing of services -->
          <CustomerApplications
            ref="customerApplicationsRef"
            :key="
              'app-' +
              (selectedApplication?.original_id || selectedApplication?.id)
            "
            :entity="selected_entity"
            :entity-type="selected_entity_type"
            :selected-application-id="
              selectedApplication?.original_id || selectedApplication?.id
            "
          />
        </div>
      </div>
    </transition>

    <div class="card rounded-3 shadow border">
      <div class="card-body">
        <CustomTable
          :fetchData="fetchApplicationsData"
          :columns="applicationColumns"
          :hasActions="true"
          :key="tableRefreshKey"
          ref="applicationsTable"
          heading="Applications"
          :expandedRowId="expandedRowId"
          :selectedRowId="selectedApplication?.id"
          :rowClass="getRowClass"
          :showCheckboxes="true"
          @expand-row="handleExpandRow"
        >
          <template #header-actions>
            <div class="d-flex align-items-center gap-2">
              <!-- Bulk Close Button -->
              <button
                v-if="selectedApplications.length > 0"
                class="btn btn-sm btn-danger rounded-3 shadow-sm"
                @click="closeBulkApplications"
                title="Close Selected Applications"
              >
                <i class="bi bi-x-circle me-1"></i>
                {{ selectedApplications.length }} selected
              </button>

              <!-- Filter Dropdown -->
              <div class="filter-dropdown-container position-relative">
                <button
                  class="filter-badge-button"
                  @click="showFilterDropdown = !showFilterDropdown"
                  title="Filter Applications"
                >
                  <i class="fas fa-filter"></i>
                  <span class="filter-badge-text">Filter</span>
                  <span
                    v-if="totalActiveFilters > 0"
                    class="filter-count-badge"
                    >{{ totalActiveFilters }}</span
                  >
                </button>

                <div v-if="showFilterDropdown" class="filter-dropdown shadow">
                  <div class="filter-header">
                    <span class="fw-bold text-xs">Filter By</span>
                    <button
                      v-if="totalActiveFilters > 0"
                      class="btn btn-sm btn-link text-xs p-0"
                      @click="clearAllFilters"
                    >
                      Clear All
                    </button>
                  </div>
                  <div class="filter-options">
                    <!-- Status Filters -->
                    <div class="filter-section-title">Status</div>
                    <label class="filter-option">
                      <input
                        type="checkbox"
                        :checked="activeFilters.includes('unread')"
                        @change="toggleFilter('unread')"
                      />
                      <span>Unread</span>
                      <span class="badge bg-primary">{{
                        statsData.unreadCount
                      }}</span>
                    </label>
                    <label class="filter-option">
                      <input
                        type="checkbox"
                        :checked="activeFilters.includes('in_progress')"
                        @change="toggleFilter('in_progress')"
                      />
                      <span>In Progress</span>
                      <span class="badge bg-warning">{{
                        statsData.inProgressCount
                      }}</span>
                    </label>
                    <label class="filter-option">
                      <input
                        type="checkbox"
                        :checked="activeFilters.includes('completed')"
                        @change="toggleFilter('completed')"
                      />
                      <span>Completed</span>
                      <span class="badge bg-success">{{
                        statsData.completedCount
                      }}</span>
                    </label>

                    <!-- Date Range Filter -->
                    <div class="filter-section-title mt-2">Date Range (Change Age)</div>
                    <div class="filter-date-range">
                      <div class="date-input-group">
                        <label class="date-label">From</label>
                        <input
                          type="date"
                          class="date-input"
                          v-model="dateFilterFrom"
                          @change="applyDateFilter"
                        />
                      </div>
                      <div class="date-input-group">
                        <label class="date-label">To</label>
                        <input
                          type="date"
                          class="date-input"
                          v-model="dateFilterTo"
                          @change="applyDateFilter"
                        />
                      </div>
                    </div>

                    <!-- Project Filter -->
                    <div class="filter-section-title mt-2">Project</div>
                    <div class="filter-searchable-dropdown" @click.stop>
                      <SearchBar
                        v-model="selectedProject"
                        mode="dropdown"
                        label="Select Project"
                        placeholder="Search projects..."
                        icon="fas fa-folder"
                        :options="projectOptions"
                        :loading="loadingProjects"
                        :hasMore="projectsHasMore"
                        @select="onProjectSelect"
                        @search="onProjectSearch"
                        @load-more="loadMoreProjects"
                      />
                    </div>

                    <!-- Workflow Filter -->
                    <div class="filter-section-title mt-2">Workflow</div>
                    <div class="filter-searchable-dropdown" @click.stop>
                      <SearchBar
                        v-model="selectedWorkflow"
                        mode="dropdown"
                        label="Select Workflow"
                        placeholder="Search workflows..."
                        icon="fas fa-project-diagram"
                        :options="workflowOptions"
                        :loading="loadingWorkflows"
                        :hasMore="workflowsHasMore"
                        @select="onWorkflowSelect"
                        @search="onWorkflowSearch"
                        @load-more="loadMoreWorkflows"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Checkbox Column -->
          <template #checkbox="{ row }">
            <input
              type="checkbox"
              :checked="isApplicationSelected(row)"
              @change="toggleApplicationSelection(row)"
              class="form-check-input"
            />
          </template>

          <template #select-all>
            <input
              type="checkbox"
              v-model="selectAll"
              @change="toggleSelectAll"
              class="form-check-input"
            />
          </template>

          <!-- Row Number Cell - by Application ID -->
          <template #cell-rownum="{ row }">
            <span class="text-muted fw-semibold">{{ row.original_id || row.id }}</span>
          </template>

          <!-- Name Cell with Unread Indicator -->
          <template #cell-name="{ row }">
            <div class="d-flex align-items-center" v-if="row">
              <div
                :class="[
                  'avatar-icon me-3',
                  row.entity_type === 'individual'
                    ? 'avatar-individual'
                    : 'avatar-company',
                ]"
              >
                <i
                  :class="
                    row.entity_type === 'individual'
                      ? 'bi bi-person'
                      : 'bi bi-building'
                  "
                ></i>
              </div>
              <div>
                <div :class="['fw-bold', { 'text-primary': row.is_unread }]">
                  {{ row.name }}
                </div>
                <small class="text-muted d-block">{{ row.customer_email }}</small>
                <small class="text-muted d-block">{{ row.customer_phone }}</small>
              </div>
            </div>
          </template>
          <!-- Change Age Cell -->
          <template #cell-change_age="{ row }">
            <div v-if="row">
              <div :class="['fw-semibold', 'text-xs', { 'text-primary': row.is_unread }]">
                {{ row.change_age || "-" }}
              </div>
              <small class="text-muted" v-if="row.last_changed">
                {{ row.last_changed }}
              </small>
            </div>
          </template>
          <!-- Fulfilled By Cell -->
          <template #cell-fulfilled_by="{ row }">
            <div v-if="row">
              <span class="fw-semibold text-xs">{{
                row.assigned_by || "-"
              }}</span>
            </div>
          </template>
          <!-- Date Created Cell -->
          <template #cell-date_created="{ row }">
            <div v-if="row">
              <small class="text-muted">
                <span class="fw-semibold">{{ row.date_created }}</span>
              </small>
            </div>
          </template>
          <!-- Asset & Workflow Cell -->
          <template #cell-asset="{ row }">
            <div v-if="row">
              <div class="fw-semibold text-xs">
                <i class="bi bi-box me-1"></i>{{ row.asset_name || "No Asset" }}
              </div>
              <small class="text-muted">
                <i class="bi bi-arrow-right-short"></i>assigned to
                <span class="fw-semibold">{{ row.workflow_name || "-" }}</span>
              </small>
              <br />
              <small class="text-muted">
                <span class="fw-semibold"
                  ><bold>Project: </bold>{{ row.project_name }}</span
                >
              </small>
            </div>
          </template>
          <!-- Reference Cell -->
          <template #cell-reference="{ row }">
            <span class="badge-soft badge-soft-secondary" v-if="row">{{
              row.reference || "-"
            }}</span>
          </template>

          <!-- Current Step Cell -->
          <template #cell-current_step="{ row }">
            <span
              v-if="row"
              @click="handleExpandRow(row.id)"
              class="badge rounded-pill bg-gray border text-dark clickable-badge"
            >
              {{ row.current_step }}
            </span>
          </template>
          <template #expandedRow="{ row }">
            <div class="expanded-row-content" v-if="row">
              <div v-if="loadingProgress[row.id]" class="text-center py-4">
                <i class="fas fa-spinner fa-spin me-2"></i>
                <span>Loading workflow progress...</span>
              </div>
              <div
                v-else-if="dropdownWorkflowProgress[row.id]"
                class="workflow-progress-bar-container"
              >
                <!-- Workflow Summary Section -->
                <div class="workflow-summary mb-4">
                  <div
                    class="d-flex flex-column flex-md-row align-items-stretch justify-content-between p-2 border rounded-3 bg-white shadow-sm"
                  >
                    <!-- Progress Section -->
                    <div
                      class="d-flex align-items-center flex-grow-1 me-md-2 mb-3 mb-md-0"
                    >
                      <div class="me-3 text-center progress-stats-container">
                        <span
                          class="text-xs text-muted text-uppercase fw-bold d-block mb-1 progress-label"
                          >Progress</span
                        >
                        <span class="fw-bold text-primary progress-value"
                          >{{
                            dropdownWorkflowProgress[row.id]
                              .progress_percentage
                          }}%</span
                        >
                      </div>
                      <div class="flex-grow-1">
                        <div class="d-flex justify-content-between mb-1">
                          <small class="text-muted text-xs"
                            >{{ row.current_step }} complete</small
                          >
                        </div>
                        <div class="progress progress-bar-container">
                          <div
                            class="workbench-progress-bar"
                            role="progressbar"
                            :style="{
                              width:
                                dropdownWorkflowProgress[row.id]
                                  .progress_percentage + '%',
                            }"
                            :aria-valuenow="
                              dropdownWorkflowProgress[row.id]
                                .progress_percentage
                            "
                            aria-valuemin="0"
                            aria-valuemax="100"
                          ></div>
                        </div>
                      </div>
                    </div>

                    <!-- Current Step -->
                    <div
                      class="d-flex align-items-center me-md-4 ps-md-2 border-start-md mb-3 mb-md-0 step-info-container"
                    >
                      <div
                        class="icon-box me-2 bg-primary-subtle text-primary rounded-circle"
                      >
                        <i class="bi bi-play-fill"></i>
                      </div>
                      <div>
                        <span
                          class="text-xs text-muted text-uppercase fw-bold d-block"
                          >Current Step</span
                        >
                        <span
                          class="fw-semibold text-sm text-dark text-truncate d-block step-name-text"
                          :title="
                            dropdownWorkflowProgress[row.id].current_step
                              ?.service_name
                          "
                        >
                          {{
                            dropdownWorkflowProgress[row.id].current_step
                              ?.service_name || "None"
                          }}
                        </span>
                      </div>
                    </div>

                    <!-- Next Service -->
                    <div
                      class="d-flex align-items-center ps-md-2 border-start-md step-info-container"
                    >
                      <div
                        class="icon-box me-2 bg-light text-muted rounded-circle"
                      >
                        <i class="bi bi-arrow-right"></i>
                      </div>
                      <div>
                        <span
                          class="text-xs text-muted text-uppercase fw-bold d-block"
                          >Next Service</span
                        >
                        <span
                          class="fw-semibold text-sm text-dark text-truncate d-block step-name-text"
                          :title="
                            dropdownWorkflowProgress[row.id].next_service
                              ?.service_name
                          "
                        >
                          {{
                            dropdownWorkflowProgress[row.id].next_service
                              ?.service_name || "Workflow complete"
                          }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Steps Visualization -->
                <div class="workflow-steps-container">
                  <template
                    v-for="(service, idx) in dropdownWorkflowProgress[row.id]
                      .services"
                    :key="service.id"
                  >
                    <!-- Step Circle -->
                    <div class="step-wrapper">
                      <div
                        :class="[
                          'step-circle-bar',
                          service.status === 'completed'
                            ? 'completed'
                            : service.status === 'in-progress'
                            ? 'current'
                            : 'pending',
                        ]"
                      >
                        <span
                          v-if="
                            service.status === 'completed' ||
                            service.status === 'in-progress'
                          "
                          >&#10003;</span
                        >
                        <span v-else>{{ idx + 1 }}</span>
                      </div>
                      <div
                        :class="[
                          'step-label-bar',
                          service.status === 'completed'
                            ? 'completed-label'
                            : service.status === 'in-progress'
                            ? 'current-label'
                            : 'pending-label',
                        ]"
                      >
                        {{ service.service_name }}
                      </div>
                    </div>
                    <!-- Connector Line (except after last step) -->
                    <div
                      v-if="
                        idx <
                        dropdownWorkflowProgress[row.id].services.length - 1
                      "
                      :key="'connector-' + service.id"
                      :class="[
                        'step-connector-bar',
                        service.status === 'completed'
                          ? 'completed-connector'
                          : 'pending-connector',
                      ]"
                    ></div>
                  </template>
                </div>
              </div>
              <div v-else class="text-center py-4 text-muted">
                No workflow progress available.
              </div>
            </div>
          </template>

          <!-- Status Cell -->
          <template #cell-status="{ row }">
            <span
              v-if="row"
              :class="[
                'badge',
                'text-white',
                row.status === 'Completed'
                  ? 'bg-success'
                  : row.status === 'In Progress'
                  ? 'bg-info'
                  : 'bg-warning',
              ]"
            >
              {{ row.status || "-" }}
            </span>
          </template>
          <!-- Actions Cell -->
          <template #actions="{ row }">
            <div v-if="row" class="d-flex align-items-center">
              <button
                class="btn btn-sm rounded-3 btn-bg-secondary me-1"
                @click="viewClient(row)"
                title="View Client"
              >
                <i class="bi bi-eye"></i>
              </button>

              <!-- Burger Menu -->
              <div
                class="custom-popup"
                :class="{ active: activePopupRowId === row.id }"
              >
                <button
                  class="btn btn-sm rounded-3 btn-bg-info me-1"
                  @click.stop="togglePopup(row.id)"
                  title="More Actions"
                >
                  <i class="bi bi-three-dots-vertical"></i>
                </button>

                <!-- Main Actions Menu -->
                <nav v-if="activePopupRowId === row.id && activeSubmenuRowId !== row.id" class="custom-popup-window">
                  <div class="popup-header">
                    <legend>Actions</legend>
                  </div>
                  <ul>
                    <li>
                      <button @click.stop="showSendSubmenu(row)">
                        <i class="bi bi-send me-2"></i>
                        Send
                        <i class="bi bi-chevron-right ms-auto"></i>
                      </button>
                    </li>
                    <li>
                      <button @click.stop="closeApplication(row)">
                        <i class="bi bi-x-circle me-2"></i>
                        Close
                      </button>
                    </li>
                    <li>
                      <button @click.stop="toggleReadStatus(row)">
                        <i :class="['me-2', row.is_unread ? 'bi bi-envelope-open' : 'bi bi-envelope']" ></i>
                        {{ row.is_unread ? 'Mark as Read' : 'Mark as Unread' }}
                      </button>
                    </li>
                  </ul>
                </nav>

                <!-- Send To Submenu -->
                <nav v-if="activePopupRowId === row.id && activeSubmenuRowId === row.id" class="custom-popup-window custom-popup-submenu-active">
                  <div class="popup-header-with-back">
                    <button class="back-button" @click.stop="hideSubmenu()">
                      <i class="bi bi-chevron-left"></i>
                    </button>
                    <span class="popup-title">Send To</span>
                  </div>
                  <ul>
                    <li>
                      <button @click.stop="sendToSeriti(row)">
                        <i class="bi bi-building me-2"></i>
                        Seriti
                      </button>
                    </li>
                    <li>
                      <button @click.stop="confirmSendApplication(row)">
                        <i class="bi bi-layers me-2"></i>
                        Sygnio
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </template>
        </CustomTable>
      </div>
    </div>
    <!-- Close Application Modal -->
    <div
      v-if="showCloseModal"
      class="modal fade show"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
    >
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content rounded-4 shadow-lg border-0">
          <div class="modal-header border-bottom-0 pb-0">
            <h5 class="modal-title fw-bold text-dark">
              {{
                selectedApplications.length > 0
                  ? `Close ${selectedApplications.length} Application(s)`
                  : "Close Application"
              }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="cancelCloseApplication"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body pt-2">
            <p class="text-muted mb-3">
              <template v-if="selectedApplications.length > 0">
                Please provide a reason for closing
                <span class="fw-bold text-dark"
                  >{{ selectedApplications.length }} selected
                  application(s)</span
                >. This action cannot be undone.
              </template>
              <template v-else>
                Please provide a reason for closing
                <span class="fw-bold text-dark">{{
                  applicationToClose?.name
                }}</span
                >. This action cannot be undone.
              </template>
            </p>
            <div class="form-group">
              <label
                for="closeReason"
                class="form-label fw-semibold text-xs text-uppercase text-muted"
                >Reason <span class="text-danger">*</span></label
              >
              <textarea
                v-model="closeReason"
                id="closeReason"
                class="form-control bg-light border-0"
                rows="5"
                placeholder="Enter reason here..."
                :class="{ 'is-invalid': closeReasonError }"
              ></textarea>
              <div v-if="closeReasonError" class="invalid-feedback">
                {{ closeReasonError }}
              </div>
            </div>
          </div>
          <div class="modal-footer border-top-0">
            <button
              type="button"
              class="btn btn-light rounded-3"
              @click="cancelCloseApplication"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-danger rounded-3 px-4"
              @click="confirmCloseApplication"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CustomerApplications from "@/layouts/data_hub/components/customer_applications/CustomerApplications.vue";
import CustomTable from "@/components/CustomTable/CustomTable.vue";
import IconCard from "@/components/CustomCard/IconCard.vue";
import SearchBar from "@/components/SearchBar/SearchBar.vue";
import { useToast } from "@/components/Toast/useToast";
import { format_date } from '@/utils/date_formatter';
import { API_BASE_URL } from "@/config";

export default {
  name: "ClientApplications",
  setup() {
    const { showSuccess, showError, showWarning } = useToast();

    return {
      showSuccess,
      showError,
      showWarning,
    };
  },
  components: {
    CustomTable,
    CustomerApplications,
    IconCard,
    SearchBar,
  },
  data: () => ({
    applicationColumns: [
      { key: "name", label: "Name" },
      { key: "status", label: "Status" },
      { key: "fulfilled_by", label: "Fulfilled By" },
      { key: "asset", label: "Workflow" },
      { key: "current_step", label: "Current Step" },
      { key: "change_age", label: "Change Age" },
      { key: "date_created", label: "Date Created" },
    ],
    selectedApplication: null,
    selected_entity: null,
    selected_entity_type: null,
    dropdownWorkflowProgress: {},
    loadingProgress: {},
    expandedRowId: null,
    showCloseModal: false,
    closeReason: "",
    closeReasonError: "",
    applicationToClose: null,
    activePopupRowId: null,
    activeSubmenuRowId: null,
    statsData: {
      inProgressCount: 0,
      unreadCount: 0,
      completedCount: 0,
      totalCount: 0,
    },
    loadingStats: false,
    showFilterDropdown: false,
    activeFilters: [],
    selectedApplications: [],
    selectAll: false,
    // New filter data
    dateFilterFrom: '',
    dateFilterTo: '',
    selectedProject: null,
    selectedWorkflow: null,
    projectOptions: [],
    workflowOptions: [],
    loadingProjects: false,
    loadingWorkflows: false,
    // Pagination state for infinite scroll
    projectsCurrentPage: 1,
    projectsTotalPages: 1,
    projectsHasMore: false,
    projectSearchQuery: '',
    workflowsCurrentPage: 1,
    workflowsTotalPages: 1,
    workflowsHasMore: false,
    workflowSearchQuery: '',
  }),
  computed: {
    totalActiveFilters() {
      let count = this.activeFilters.length;
      if (this.dateFilterFrom || this.dateFilterTo) count++;
      if (this.selectedProject) count++;
      if (this.selectedWorkflow) count++;
      return count;
    },
  },
  methods: {
    formatDate(date) {
      return format_date(date);
    },

    calculateChangeAge(lastChanged) {
      if (!lastChanged) return null;
      const now = new Date();
      const changed = new Date(lastChanged);
      const diffMs = now - changed;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor(diffMs / (1000 * 60));

      if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
      if (diffHours > 0)
        return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
      if (diffMinutes > 0)
        return `${diffMinutes} min${diffMinutes > 1 ? "s" : ""} ago`;
      return "Just now";
    },

    getRowClass(row) {
      if (row.is_unread) {
        return "unread-row";
      }
      return "";
    },
    fetchApplicationsData(page = 1, search = "") {
      const TOKEN = localStorage.getItem("token");
      const params = new URLSearchParams({ page });

      if (search) params.append("search", search);

      const filterMap = {
        unread: "is_unread=true",
        in_progress: "status=in_progress",
        completed: "status=completed"
      };

      this.activeFilters.forEach(filter => {
        if (filterMap[filter]) params.append(...filterMap[filter].split("="));
      });

      // Date range filters
      if (this.dateFilterFrom) params.append("date_from", this.dateFilterFrom);
      if (this.dateFilterTo) params.append("date_to", this.dateFilterTo);

      // Project filter
      if (this.selectedProject) params.append("project_id", this.selectedProject);

      // Workflow filter
      if (this.selectedWorkflow) params.append("workflow_id", this.selectedWorkflow);

      const url = `${API_BASE_URL}/verify_api/data_hub/applications/?${params.toString()}`;

      return new Promise((resolve, reject) => {
        $.ajax({
          url: url,
          method: "GET",
          dataType: "json",
          headers: {
            Authorization: `Token ${TOKEN}`,
          },
          success: (data) => {
            const appList = data.results || data || [];

            const applications = appList.map((app) => {
              const detail = app.customer_applications_detail || {};
              const assetInfo = detail.asset_info || {};
              const appState = detail.application_state || {};
              const primaryEntity = app.primary_entity || {};

              return {
                id: app.id,
                original_id: app.id,
                name: app.entity_name || primaryEntity.name || "Unknown",
                entity_type: app.entity_type || primaryEntity.entity_type || "individual",
                entity_id: app.entity_id || primaryEntity.id,
                customer_id: app.customer_id,
                customer_email: app.customer_email || "-",
                customer_phone: app.customer_phone || "-",
                date_created: this.formatDate(app.date_created),
                asset_name: assetInfo.asset_name || "No Asset",
                asset_id: assetInfo.asset_id || null,
                workflow_name: app.workflow_name || "",
                project_name: app.project_name || "-",
                workflow_id: app.workflow_id || null,
                reference: appState.lead_reference || detail.reference || "",
                current_step: detail.current_step || "-",
                status: (app.application_status === "Completed") ? "Completed" : "In Progress",
                is_unread: appState.is_unread || app.has_unread_applications || false,
                is_closed: appState.is_closed || false,
                is_fulfilled: appState.is_fulfilled || false,
                assigned_by: appState.assigned_by_name || "-",
                last_changed: this.formatDate(appState.last_changed),
                change_age: this.calculateChangeAge(appState.last_changed),
                last_changed_by: appState.last_changed_by_name || "-",
                has_unread_applications: app.has_unread_applications || false,
                unread_applications_count: app.unread_applications_count || 0,
                entities: app.entities || [],
                primary_entity: primaryEntity,
              };
            });

            this.fetchStats();

            resolve({
              results: applications,
              count: data.count || applications.length,
              total_pages: data.total_pages || 1,
              current_page: page,
            });
          },
          error: (xhr, status, error) => {
            reject(error);
          },
        });
      });
    },

    fetchStats() {
      if (this.loadingStats) return;

      this.loadingStats = true;
      const TOKEN = localStorage.getItem("token");
      const url = `${API_BASE_URL}/verify_api/customer_management_api/customer_application_stats/`;

      $.ajax({
        url,
        method: "GET",
        dataType: "json",
        headers: { Authorization: `Token ${TOKEN}` },
        success: (response) => {
          this.statsData = {
            inProgressCount: response.in_progress || 0,
            unreadCount: response.unread || 0,
            completedCount: response.completed || 0,
            totalCount: response.total || 0,
          };
          this.$store.dispatch("update_unread_workbench_count", response.unread || 0);
          this.loadingStats = false;
        },
        error: () => {
          this.loadingStats = false;
        },
      });
    },

    viewClient(row) {
      this.markAsRead(row);
      this.selectedApplication = row;
      this.selected_entity = row.primary_entity || {
        id: row.entity_id,
        name: row.name,
      };
      this.selected_entity_type = row.entity_type || "-";

      this.$nextTick(() => {
        const wrapper = document.querySelector(".page-content-wrapper");
        if (wrapper) {
          wrapper.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      });
      this.loadWorkflowProgress(row);
    },

    openApplicationPanel(row) {
      this.selectedApplication = row;
      this.selected_entity = row.primary_entity || {
        id: row.entity_id,
        name: row.name,
      };
      this.selected_entity_type = row.entity_type || "individual";

      this.$nextTick(() => {
        const wrapper = document.querySelector(".page-content-wrapper");
        if (wrapper) {
          wrapper.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      });

      this.loadWorkflowProgress(row);
    },

    closeApplicationView() {
      this.selectedApplication = null;
      this.selected_entity = null;
      this.selected_entity_type = null;
    },

    markAsRead(application) {
      const TOKEN = localStorage.getItem("token");
      const applicationId = application.original_id || application.id;

      $.ajax({
        url: `${API_BASE_URL}/verify_api/customer_management_api/application/${applicationId}/read/`,
        method: "POST",
        headers: { Authorization: `Token ${TOKEN}` },
        success: (response) => {
          if (response.status === "Success") {
            application.is_unread = false;
            const table = this.$refs.applicationsTable;
            if (table?.rows) {
              const row = table.rows.find(r => r.id === application.id || r.original_id === applicationId);
              if (row) row.is_unread = false;
            }
            this.statsData.unreadCount = Math.max(0, this.statsData.unreadCount - 1);
            this.fetchStats();
          }
        },
      });
    },

    toggleReadStatus(row) {
      this.activePopupRowId = null;
      this.activeSubmenuRowId = null;
      
      if (row.is_unread) {
        this.markAsRead(row);
        this.showSuccess("Application marked as read");
      } else {
        this.markAsUnread(row);
      }
    },

    markAsUnread(row) {
      const TOKEN = localStorage.getItem("token");
      const applicationId = row.original_id || row.id;

      $.ajax({
        url: `${API_BASE_URL}/verify_api/customer_management_api/application/${applicationId}/unread/`,
        method: "POST",
        headers: { Authorization: `Token ${TOKEN}` },
        success: (response) => {
          if (response.status === "Success") {
            row.is_unread = true;
            const table = this.$refs.applicationsTable;
            if (table?.rows) {
              const tableRow = table.rows.find(r => r.id === row.id || r.original_id === applicationId);
              if (tableRow) tableRow.is_unread = true;
            }
            this.fetchStats();
            this.showSuccess("Application marked as unread");
          }
        },
        error: () => {
          this.showError("Failed to mark application as unread");
        },
      });
    },

    loadWorkflowProgress(application) {
      const { workflow_id: workflowId, original_id, id } = application;
      if (!workflowId) return;

      const TOKEN = localStorage.getItem("token");
      const applicationId = original_id || id;
      const url = `${API_BASE_URL}/verify_api/customer_management_api/get_workflow_progress/${applicationId}/${workflowId}/`;

      $.ajax({
        url,
        method: "GET",
        dataType: "json",
        headers: { Authorization: `Token ${TOKEN}` },
        success: (response) => {
          if (response.status === "success") {
            this.$nextTick(() => {
              const progressPanel = document.querySelector(".workflow-progress-panel");
              progressPanel?.scrollIntoView({ behavior: "smooth", block: "start" });
            });
          }
        },
        error: () => this.showError("Failed to load workflow progress"),
      });
    },

    loadDropdownWorkflowProgress(application) {
      const { workflow_id: workflowId, original_id, id } = application;
      const applicationId = original_id || id;

      if (!workflowId) return;

      this.loadingProgress[id] = true;
      const TOKEN = localStorage.getItem("token");
      const url = `${API_BASE_URL}/verify_api/customer_management_api/get_workflow_progress/${applicationId}/${workflowId}/`;

      $.ajax({
        url,
        method: "GET",
        dataType: "json",
        headers: { Authorization: `Token ${TOKEN}` },
        success: (response) => {
          if (response.status === "success") {
            this.dropdownWorkflowProgress[id] = {
              progress_percentage: response.progress_percentage || 0,
              completed_services: response.completed_services || 0,
              total_services: response.total_services || 0,
              current_step: response.current_step,
              next_service: response.next_service,
              services: response.services || [],
              is_completed: response.is_completed,
            };
          }
          this.loadingProgress[id] = false;
        },
        error: () => {
          this.loadingProgress[id] = false;
        },
      });
    },

    togglePopup(rowId) {
      if (this.activePopupRowId === rowId) {
        this.activePopupRowId = null;
        this.activeSubmenuRowId = null;
      } else {
        this.activePopupRowId = rowId;
        this.activeSubmenuRowId = null;
      }
    },

    showSendSubmenu(row) {
      this.activeSubmenuRowId = row.id;
    },

    hideSubmenu() {
      this.activeSubmenuRowId = null;
    },

    confirmSendApplication(row) {
      this.activePopupRowId = null;
      this.activeSubmenuRowId = null;
      this.openApplicationPanel(row);
    },

    sendToSeriti(row) {
      this.activePopupRowId = null;
      this.activeSubmenuRowId = null;
      this.openApplicationPanel(row);
      
      this.$nextTick(() => {
        setTimeout(() => {
          const customerApps = this.$refs.customerApplicationsRef;
          if (customerApps && typeof customerApps.openSeritiPolicyDirect === 'function') {
            customerApps.openSeritiPolicyDirect();
          }
        }, 300);
      });
    },

    resetCloseModal() {
      this.showCloseModal = false;
      this.applicationToClose = null;
      this.selectedApplications = [];
      this.closeReason = "";
      this.closeReasonError = "";
    },

    closeApplication(row) {
      this.activePopupRowId = null;
      this.activeSubmenuRowId = null;
      this.applicationToClose = row;
      this.selectedApplications = [];
      this.closeReason = "";
      this.closeReasonError = "";
      this.showCloseModal = true;
    },

    closeBulkApplications() {
      if (this.selectedApplications.length === 0) {
        this.showWarning("Please select applications to close");
        return;
      }
      this.applicationToClose = null;
      this.closeReason = "";
      this.closeReasonError = "";
      this.showCloseModal = true;
    },

    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedApplications = [...this.$refs.applicationsTable.rows];
      } else {
        this.selectedApplications = [];
      }
    },

    toggleApplicationSelection(row) {
      const index = this.selectedApplications.findIndex(
        (app) => app.id === row.id
      );
      if (index > -1) {
        this.selectedApplications.splice(index, 1);
      } else {
        this.selectedApplications.push(row);
      }
      // Update selectAll state
      this.selectAll =
        this.$refs.applicationsTable?.rows &&
        this.selectedApplications.length ===
          this.$refs.applicationsTable.rows.length;
    },

    isApplicationSelected(row) {
      return this.selectedApplications.some((app) => app.id === row.id);
    },

    toggleFilter(filterName) {
      const index = this.activeFilters.indexOf(filterName);
      if (index > -1) {
        this.activeFilters.splice(index, 1);
      } else {
        this.activeFilters.push(filterName);
      }
      this.$refs.applicationsTable?.reload?.();
    },

    clearAllFilters() {
      this.activeFilters = [];
      this.dateFilterFrom = '';
      this.dateFilterTo = '';
      this.selectedProject = null;
      this.selectedWorkflow = null;
      this.$refs.applicationsTable?.reload?.();
    },

    applyDateFilter() {
      this.$refs.applicationsTable?.reload?.();
    },

    // Project filter methods
    fetchProjects(page = 1, search = "", append = false) {
      if (this.loadingProjects) return;
      
      this.loadingProjects = true;
      const TOKEN = localStorage.getItem("token");
      let url = `${API_BASE_URL}/verify_api/workflow_management_api/get_projects_api/?page=${page}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }

      $.ajax({
        url,
        method: "GET",
        dataType: "json",
        headers: { Authorization: `Token ${TOKEN}` },
        success: (data) => {
          const projects = data.results || [];
          const newOptions = Array.isArray(projects) 
            ? projects.map(project => ({
                value: project.id,
                label: project.project_name || project.name
              }))
            : [];
          
          if (append && page > 1) {
            // Append to existing options for infinite scroll
            this.projectOptions = [...this.projectOptions, ...newOptions];
          } else {
            // Replace options for new search or initial load
            this.projectOptions = newOptions;
          }
          
          // Update pagination state
          this.projectsCurrentPage = page;
          this.projectsTotalPages = data.total_pages || Math.ceil((data.count || 0) / 10) || 1;
          this.projectsHasMore = page < this.projectsTotalPages;
          
          this.loadingProjects = false;
        },
        error: () => {
          if (!append) {
            this.projectOptions = [];
          }
          this.loadingProjects = false;
        },
      });
    },

    loadMoreProjects() {
      if (this.projectsHasMore && !this.loadingProjects) {
        this.fetchProjects(this.projectsCurrentPage + 1, this.projectSearchQuery, true);
      }
    },

    onProjectSearch(query) {
      // Reset pagination on new search
      this.projectSearchQuery = query;
      this.projectsCurrentPage = 1;
      this.fetchProjects(1, query, false);
    },

    onProjectSelect() {
      this.$refs.applicationsTable?.reload?.();
    },

    // Workflow filter methods
    fetchWorkflows(page = 1, search = "", append = false) {
      if (this.loadingWorkflows) return;
      
      this.loadingWorkflows = true;
      const TOKEN = localStorage.getItem("token");
      let url = `${API_BASE_URL}/verify_api/verify_admin_api/get_workflows/?page=${page}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }

      $.ajax({
        url,
        method: "GET",
        dataType: "json",
        headers: { Authorization: `Token ${TOKEN}` },
        success: (data) => {
          // API returns { active_workflows: [...] }
          const workflows = data.active_workflows || data.results || [];
          const newOptions = Array.isArray(workflows)
            ? workflows.map(workflow => ({
                value: workflow.id,
                label: workflow.workflow_name || workflow.name
              }))
            : [];
          
          if (append && page > 1) {
            // Append to existing options for infinite scroll
            this.workflowOptions = [...this.workflowOptions, ...newOptions];
          } else {
            // Replace options for new search or initial load
            this.workflowOptions = newOptions;
          }
          
          // Update pagination state
          this.workflowsCurrentPage = page;
          this.workflowsTotalPages = data.total_pages || Math.ceil((data.count || 0) / 10) || 1;
          this.workflowsHasMore = page < this.workflowsTotalPages;
          
          this.loadingWorkflows = false;
        },
        error: () => {
          if (!append) {
            this.workflowOptions = [];
          }
          this.loadingWorkflows = false;
        },
      });
    },

    loadMoreWorkflows() {
      if (this.workflowsHasMore && !this.loadingWorkflows) {
        this.fetchWorkflows(this.workflowsCurrentPage + 1, this.workflowSearchQuery, true);
      }
    },

    onWorkflowSearch(query) {
      this.workflowSearchQuery = query;
      this.workflowsCurrentPage = 1;
      this.fetchWorkflows(1, query, false);
    },

    onWorkflowSelect() {
      this.$refs.applicationsTable?.reload?.();
    },

    handleFilterClickOutside(event) {
      if (this.showFilterDropdown) {
        const target = event.target;
        if (!target.closest(".filter-dropdown-container")) {
          this.showFilterDropdown = false;
        }
      }
    },

    handleClickOutside(event) {
      if (this.activePopupRowId !== null) {
        const target = event.target;
        // Check if the click is inside a popup or on a toggle button
        if (!target.closest(".custom-popup")) {
          this.activePopupRowId = null;
          this.activeSubmenuRowId = null;
        }
      }
    },

    cancelCloseApplication() {
      this.resetCloseModal();
    },

    confirmCloseApplication() {
      if (!this.closeReason.trim()) {
        this.closeReasonError = "Reason is required.";
        return;
      }

      const TOKEN = localStorage.getItem("token");
      const payload = this.selectedApplications.length > 0
        ? {
            application_ids: this.selectedApplications.map(app => app.original_id || app.id),
            reason: this.closeReason,
          }
        : {
            application_id: this.applicationToClose.original_id || this.applicationToClose.id,
            reason: this.closeReason,
          };

      $.ajax({
        url: `${API_BASE_URL}/verify_api/customer_management_api/applications/close/`,
        method: "POST",
        headers: {
          Authorization: `Token ${TOKEN}`,
          "Content-Type": "application/json",
        },
        data: JSON.stringify(payload),
        success: (response) => {
          if (
            response.status === "Success" ||
            response.status === "partial_success"
          ) {
            let message = "Application(s) closed successfully.";

            if (response.total_successful !== undefined) {
              const successCount = response.total_successful;
              const failedCount = response.total_failed;

              message = `Successfully closed ${successCount} application(s)`;
              if (failedCount > 0) {
                message += `. Failed to close ${failedCount} application(s)`;
              }
            }

            this.resetCloseModal();
            if (
              this.selectedApplication &&
              this.applicationToClose &&
              this.selectedApplication?.id === this.applicationToClose.id
            ) {
              this.closeApplicationView();
            }
            this.showSuccess(message);

            this.$refs.applicationsTable?.loadData?.();
            this.fetchStats();
          }
        },
        error: (xhr) => {
          const errorMessage =
            xhr.responseJSON?.message || "Failed to close application(s)";
          this.showError(errorMessage);
        },
      });
    },

    handleExpandRow(rowId) {
      this.expandedRowId = this.expandedRowId === rowId ? null : rowId;

      if (this.expandedRowId && !this.dropdownWorkflowProgress[rowId]) {
        const row = this.findRowById(rowId);
        if (row) this.loadDropdownWorkflowProgress(row);
      }
    },

    findRowById(rowId) {
      return this.$refs.applicationsTable?.rows?.find(row => row.id === rowId) || null;
    },
    handleSearchTrigger() {
      const { customer_id, search_trigger } = this.$route.query;
      
      if (search_trigger === 'true' && customer_id) {
        this.fetchCustomerApplicationFromSearch(customer_id);
        
        this.$router.replace({ 
          name: 'workbench', 
          query: {} 
        });
      }
    },
    async fetchCustomerApplicationFromSearch(customerId) {
      try {
        const result = await this.fetchApplicationsData(1, customerId);
        
        if (result.results && result.results.length > 0) {
          this.viewClient(result.results[0]);
        }
      } catch (error) {
        console.error("Failed to fetch customer application:", error);
      }
    },
  },
  mounted() {
    this.fetchStats();
    this.fetchProjects();
    this.fetchWorkflows();
    document.addEventListener("click", this.handleClickOutside);
    document.addEventListener("click", this.handleFilterClickOutside);

    this.handleSearchTrigger();
  },
  watch: {
    '$route.query'(newQuery) {
      if (newQuery.search_trigger === 'true' && newQuery.customer_id) {
        this.handleSearchTrigger();
      }
    }
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    document.removeEventListener("click", this.handleFilterClickOutside);
  },
};
</script>

<style scoped>
/* Animations */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Avatar Icons for Table */
.avatar-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.avatar-icon i {
  font-size: 20px;
}

.avatar-individual {
  background: linear-gradient(
    135deg,
    rgba(13, 110, 253, 0.15) 0%,
    rgba(13, 110, 253, 0.05) 100%
  );
  color: #0d6efd;
  border: 2px solid rgba(13, 110, 253, 0.2);
}

.avatar-company {
  background: linear-gradient(
    135deg,
    rgba(25, 135, 84, 0.15) 0%,
    rgba(25, 135, 84, 0.05) 100%
  );
  color: #198754;
  border: 2px solid rgba(25, 135, 84, 0.2);
}

/* Badges */
.badge-soft {
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
}

.badge-soft-primary {
  background: rgba(13, 110, 253, 0.1);
  color: #0d6efd;
}

.badge-soft-success {
  background: rgba(25, 135, 84, 0.1);
  color: #198754;
}

.badge-soft-secondary {
  background: rgba(108, 117, 125, 0.1);
  color: #6c757d;
}

/* Unread Row Styling - Border indicator like selected-row */
:deep(.unread-row) {
  border-left: 3px solid var(--primary-color, #0d6efd) !important;
}

:deep(.unread-row td) {
  font-weight: 600;
}

/* Selected Row - Darker primary gradient (no border, just highlight) */
:deep(.selected-row) {
  background: linear-gradient(
    90deg,
    rgba(var(--primary-color-rgb, 13, 110, 253), 0.25) 0%,
    rgba(var(--primary-color-rgb, 13, 110, 253), 0.1) 100%
  ) !important;
}

:deep(.selected-row td) {
  background-color: transparent !important;
}

/* Text Utilities */
.text-xs {
  font-size: 0.8rem;
}

.fw-semibold {
  font-weight: 600;
}

/* Expanded Row Styling */
.expanded-row-content {
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 8px;
  margin: 8px 0;
}

/* Step Circle Bar for Workflow Progress */
.step-circle-bar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid #dee2e6;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 8px;
  transition: background 0.2s, border-color 0.2s;
}

.step-circle-bar.completed {
  background: var(--primary-color, #1976d2);
  border-color: var(--primary-color, #1976d2);
  color: #fff;
}

.step-circle-bar.current {
  background: #fff;
  border-color: var(--primary-color, #1976d2);
  color: var(--primary-color, #1976d2);
  box-shadow: 0 0 0 4px rgba(var(--primary-color-rgb, 25, 118, 210), 0.15);
}

.step-circle-bar.pending {
  background: #fff;
  border-color: #dee2e6;
  color: #adb5bd;
}

/* Step Label Bar */
.step-label-bar {
  font-size: 0.95rem;
  text-align: center;
  margin-top: 2px;
  min-width: 80px;
  color: #212529;
  font-weight: 500;
}

.step-label-bar.completed-label {
  color: black;
}

.step-label-bar.current-label {
  color: var(--primary-color);
  font-weight: bold;
}

.step-label-bar.pending-label {
  color: #adb5bd;
}

/* Step Connector Bar */
.step-connector-bar {
  height: 4px;
  width: 45px;
  background: transparent;
  margin: 0 -10px;
  position: relative;
  z-index: 1;
  align-self: center;
}

.step-connector-bar.completed-connector {
  background: transparent;
}

.step-connector-bar.pending-connector {
  background: transparent;
}

/* Clickable Elements */
.clickable-link {
  cursor: pointer;
  color: #0d6efd;
  text-decoration: underline;
}

.clickable-badge {
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 10px;
}

.clickable-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Workflow Progress Container */
.workflow-steps-container {
  display: flex;
  align-items: center;
  overflow-x: auto;
  padding: 8px 0;
}

.step-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 100px;
}

/* Workbench Progress Bar */
.workbench-progress-bar {
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(var(--primary-color-rgb), 0.5) 0%,
    var(--primary-color) 100%
  );
  border-radius: 6px;
  transition: width 0.3s ease;
  box-shadow: 0 2px 4px rgba(var(--primary-color-rgb, 13, 110, 253), 0.2);
}

.icon-box {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.bg-primary-subtle {
  background-color: rgba(var(--primary-color-rgb, 13, 110, 253), 0.1);
}

.text-primary {
  color: var(--primary-color) !important;
}

/* Responsive Utilities */
@media (min-width: 768px) {
  .border-start-md {
    border-left: 1px solid rgb(153, 153, 153) !important;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .expanded-row-content {
    padding: 16px;
  }
}

.progress-bar-container {
  height: 6px;
  width: 95%;
}

.step-info-container {
  min-width: 200px;
}

.step-name-text {
  max-width: 350px;
  font-size: 0.75rem;
}

.icon-box {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
}

.progress-label {
  font-size: 0.65rem;
}

.progress-value {
  font-size: 1rem;
}

/* Modal Styles */
.modal.show {
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
}

/* Custom Popup Styles */
.custom-popup {
  display: inline-block;
  text-rendering: optimizeLegibility;
  position: relative;
  --nav-bg: #ffffff;
  --nav-border-radius: 0.75em;
  --nav-border-color: #e9ecef;
  --nav-border-width: 1px;
  --nav-shadow-color: rgba(0, 0, 0, 0.1);
  --nav-shadow-width: 0 0.25rem 0.5rem;
  --nav-padding-x: 0.75em;
  --nav-padding-y: 1em;
  --nav-title-color: #6c757d;
  --nav-title-size: 0.75em;
  --nav-title-padding-x: 1rem;
  --nav-title-padding-y: 0.5em;
  --nav-button-padding-x: 1rem;
  --nav-button-padding-y: 0.5em;
  --nav-button-border-radius: 0.375em;
  --nav-button-font-size: 12px;
  --nav-button-distance: 0.75em;
}

.custom-popup-window {
  transform: scale(0.95);
  visibility: hidden;
  opacity: 0;
  position: absolute;
  padding: var(--nav-padding-y) var(--nav-padding-x);
  background: var(--nav-bg);
  color: #495057;
  border-radius: var(--nav-border-radius);
  box-shadow: var(--nav-shadow-width) var(--nav-shadow-color);
  border: var(--nav-border-width) solid var(--nav-border-color);
  top: 100%;
  right: 0;
  margin-top: 5px;
  transition: all 0.1s ease-in-out;
  z-index: 999;
  min-width: 180px;
}

.custom-popup.active .custom-popup-window {
  transform: scale(1);
  visibility: visible;
  opacity: 1;
}

.custom-popup-window.custom-popup-submenu-active {
  transform: scale(1);
  visibility: visible;
  opacity: 1;
}

.custom-popup-window legend {
  padding: var(--nav-title-padding-y) var(--nav-title-padding-x);
  margin: 0;
  color: var(--nav-title-color);
  font-size: var(--nav-title-size);
  text-transform: uppercase;
  font-weight: 600;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 5px;
  width: 100%;
}

.custom-popup-window ul {
  margin: 0;
  padding: 0;
  list-style-type: none;
}

.custom-popup-window ul button {
  outline: none;
  width: 100%;
  border: none;
  background: none;
  display: flex;
  align-items: center;
  color: #495057;
  font-size: var(--nav-button-font-size);
  padding: var(--nav-button-padding-y) var(--nav-button-padding-x);
  white-space: nowrap;
  border-radius: var(--nav-button-border-radius);
  cursor: pointer;
  column-gap: var(--nav-button-distance);
  transition: all 0.2s ease;
}

.custom-popup-window ul button i {
  color: var(--primary-color);
}

.custom-popup-window ul button:hover {
  background: rgba(13, 110, 253, 0.1);
  color: #0d6efd;
}

.custom-popup-window ul button .ms-auto {
  margin-left: auto;
  font-size: 0.7rem;
}

/* Submenu Animation */
.custom-popup-submenu-active {
  animation: slideInFromRight 0.2s ease-out;
}

@keyframes slideInFromRight {
  from {
    opacity: 0;
    transform: translateX(10px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

/* Popup Header with Back Button */
.popup-header {
  display: flex;
  align-items: center;
  padding: var(--nav-title-padding-y) var(--nav-title-padding-x);
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 5px;
}

.popup-header legend {
  padding: 0;
  margin: 0;
  border: none;
  color: var(--nav-title-color);
  font-size: var(--nav-title-size);
  text-transform: uppercase;
  font-weight: 600;
}

.custom-popup.active .custom-popup-window .popup-header-with-back,
.custom-popup .custom-popup-window.custom-popup-submenu-active .popup-header-with-back {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  padding: 0.5em 0.75em;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 5px;
}

.custom-popup.active .custom-popup-window .popup-header-with-back > .back-button,
.custom-popup .custom-popup-window.custom-popup-submenu-active .popup-header-with-back > .back-button {
  all: unset !important;
  background: none !important;
  border: none !important;
  color: var(--primary-color, #0d6efd) !important;
  cursor: pointer !important;
  padding: 4px !important;
  margin-right: 4px !important;
  font-size: 0.85rem !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  transition: background 0.2s ease !important;
  flex-shrink: 0 !important;
  width: auto !important;
  border-radius: 4px !important;
}

.custom-popup.active .custom-popup-window .popup-header-with-back > .back-button:hover,
.custom-popup .custom-popup-window.custom-popup-submenu-active .popup-header-with-back > .back-button:hover {
  background: rgba(13, 110, 253, 0.1) !important;
}

.custom-popup.active .custom-popup-window .popup-header-with-back > .back-button i,
.custom-popup .custom-popup-window.custom-popup-submenu-active .popup-header-with-back > .back-button i {
  font-size: 0.75rem !important;
  color: var(--primary-color, #0d6efd) !important;
}

.custom-popup.active .custom-popup-window .popup-header-with-back > .popup-title,
.custom-popup .custom-popup-window.custom-popup-submenu-active .popup-header-with-back > .popup-title {
  color: #6c757d;
  font-size: 0.75em;
  text-transform: uppercase;
  font-weight: 600;
  margin: 0;
  padding: 0;
  white-space: nowrap;
}

/* Stats Card Wrapper Styling */
.stats-card-wrapper {
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.stats-card-wrapper :deep(.card) {
  transition: all 0.3s ease;
}

.stats-card-wrapper.stats-card-active :deep(.card) {
  border: 2px solid var(--primary-color, #0d6efd) !important;
  box-shadow: 0 2px 8px rgba(var(--primary-color-rgb, 13, 110, 253), 0.15);
}

.stats-card-wrapper:hover :deep(.card) {
  border-color: rgba(var(--primary-color-rgb, 13, 110, 253), 0.3);
}

/* Ensure table overflow allows popup to be seen */
:deep(.table-responsive) {
  overflow: visible !important;
}

/* Application View Card Styling */
.application-view-card {
  border-radius: 0.75rem !important;
  overflow: hidden;
}

.application-view-card .card-body {
  border-radius: inherit;
}

/* Notes Button Trigger */
.btn-notes-trigger {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  font-weight: 600;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-notes-trigger:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(99, 102, 241, 0.4);
  color: white;
}

.btn-notes-trigger:active {
  transform: translateY(0);
}

.btn-notes-trigger i {
  font-size: 16px;
}

/* Notes Modal Overlay */
.notes-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.7);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

/* Notes Modal Container */
.notes-modal-container {
  width: 100%;
  max-width: 1400px;
  max-height: 95vh;
  background: #f8fafc;
  border-radius: 20px;
  box-shadow: 0 25px 60px -15px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.notes-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
}

.notes-modal-header h5 {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.notes-modal-body {
  flex: 1;
  overflow: hidden;
}

.notes-modal-body :deep(.notes-app) {
  min-height: 80vh;
  border-radius: 0;
}

/* Notes Modal Animation */
.notes-modal-fade-enter-active,
.notes-modal-fade-leave-active {
  transition: all 0.3s ease;
}

.notes-modal-fade-enter-from,
.notes-modal-fade-leave-to {
  opacity: 0;
}

.notes-modal-fade-enter-from .notes-modal-container,
.notes-modal-fade-leave-to .notes-modal-container {
  transform: scale(0.95) translateY(20px);
  opacity: 0;
}

.notes-modal-fade-enter-active .notes-modal-container {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.notes-modal-fade-leave-active .notes-modal-container {
  transition: all 0.2s ease-out;
}

/* Filter Dropdown Styles */
.filter-dropdown-container {
  display: inline-block;
  margin-right: 0.75rem;
}

.filter-badge-button {
  background: #f8f9fa;
  border: 1.5px solid #dee1e5;
  border-radius: 1rem;
  padding: 0.4rem 0.6rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  position: relative;
}

.filter-badge-button:hover {
  background: white;
  border-color: #ced4da;
  color: #495057;
}

.filter-badge-button i {
  font-size: 0.875rem;
}

.filter-badge-text {
  user-select: none;
}

.filter-count-badge {
  background: var(--primary-color, #0d6efd);
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  margin-left: 0.15rem;
}

.filter-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(0, 0, 0, 0.08);
  min-width: 240px;
  max-height: 600px;
  overflow-y: visible;
  overflow-x: visible;
  z-index: 1000;
  animation: filterFadeIn 0.2s ease;
}

/* Scrollbar styling for filter dropdown */
.filter-dropdown::-webkit-scrollbar {
  width: 6px;
}

.filter-dropdown::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.filter-dropdown::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.filter-dropdown::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

@keyframes filterFadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.filter-header .fw-bold {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-header .btn-link {
  font-size: 0.75rem;
  text-decoration: none;
  color: var(--primary-color, #0d6efd);
}

.filter-options {
  padding: 8px 0;
  overflow: visible;
}

.filter-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-option:hover {
  background: rgba(var(--primary-color-rgb, 13, 110, 253), 0.08);
}

.filter-option input[type="checkbox"] {
  margin-right: 10px;
  cursor: pointer;
}

.filter-option span:first-of-type {
  flex: 1;
  font-size: 0.8rem;
  font-weight: 500;
  color: #212529;
}

.filter-option .badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

/* Filter Section Title */
.filter-section-title {
  font-size: 0.65rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 8px 16px 4px;
  margin-top: 4px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.filter-section-title:first-child {
  border-top: none;
  margin-top: 0;
}

/* Date Range Filter Styles */
.filter-date-range {
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.date-input-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.date-label {
  font-size: 0.75rem;
  color: #6c757d;
  min-width: 40px;
}

.date-input {
  flex: 1;
  padding: 6px 10px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 0.8rem;
  color: #495057;
  background: #f8f9fa;
  transition: all 0.2s ease;
}

.date-input:focus {
  outline: none;
  border-color: var(--primary-color, #0d6efd);
  background: white;
  box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb, 13, 110, 253), 0.1);
}

.date-input::-webkit-calendar-picker-indicator {
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.date-input::-webkit-calendar-picker-indicator:hover {
  opacity: 1;
}

/* Searchable Dropdown Filter Styles */
.filter-searchable-dropdown {
  padding: 8px 16px;
  position: relative;
}

.filter-searchable-dropdown :deep(.search-badge-wrapper) {
  width: 100%;
  position: static;
}

.filter-searchable-dropdown :deep(.search-badge) {
  width: 100%;
  justify-content: flex-start;
}

.filter-searchable-dropdown :deep(.search-badge-dropdown) {
  min-width: 100%;
}

.filter-searchable-dropdown :deep(.search-dropdown-menu) {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 9999 !important;
  max-height: 200px;
  overflow-y: auto;
}
</style>
